import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { InGameCurrency } from '__generated__/globalTypes';
import fiftyFiftyCraftClueUrl from 'assets/crafting-clues/best-five.webp';
import bestStarRankCraftClueUrl from 'assets/crafting-clues/best-star-rank.webp';
import competitionCraftClueUrl from 'assets/crafting-clues/competition.webp';
import divisionClueUrl from 'assets/crafting-clues/division.webp';
import countryCraftClueUrl from 'assets/crafting-clues/nationality.webp';
import positionClueUrl from 'assets/crafting-clues/position.webp';
import potionCraftClueUrl from 'assets/crafting-clues/potion.webp';
import { lazy } from 'lib/retry';

import type {
  InGameCurrency3d_ProbabilisticBundleSlotInGameCurrencyItem,
  InGameCurrency3d_inGameCurrencyReward,
} from './__generated__/index.graphql';
import wheelTicketUrl from './assets/wheel-ticket.webp';

const Energy = lazy(
  async () => import('@sorare/trois/src/atoms/Energy'),
  'Energy'
);

const Picture = lazy(
  async () => import('@sorare/trois/src/atoms/Picture'),
  'Picture'
);

const XP = lazy(async () => import('@sorare/trois/src/atoms/XP'), 'XP');
type Props = {
  item:
    | InGameCurrency3d_ProbabilisticBundleSlotInGameCurrencyItem
    | InGameCurrency3d_inGameCurrencyReward;
};

export const InGameCurrency3d = ({ item: { currency } }: Props) => {
  return {
    // energy
    [InGameCurrency.LIMITED_ENERGY]: <Energy rarity="limited" />,
    [InGameCurrency.RARE_ENERGY]: <Energy rarity="rare" />,
    [InGameCurrency.SUPER_RARE_ENERGY]: <Energy rarity="super_rare" />,
    [InGameCurrency.UNIQUE_ENERGY]: <Energy rarity="unique" />,

    // xp
    [InGameCurrency.COMMON_XP]: <XP rarity="common" />,
    [InGameCurrency.LIMITED_XP]: <XP rarity="limited" />,
    [InGameCurrency.RARE_XP]: <XP rarity="rare" />,
    [InGameCurrency.SUPER_RARE_XP]: <XP rarity="super_rare" />,
    [InGameCurrency.UNIQUE_XP]: <XP rarity="unique" />,

    // FIXME
    [InGameCurrency.OLD_CLUB_SHOP_COIN]: null,
    [InGameCurrency.COMMON_GEM]: null,
    [InGameCurrency.GOLDEN_WHEEL_TICKET]: null,
    [InGameCurrency.COMMON_GOLDEN_WHEEL_TICKET]: null,
    [InGameCurrency.COMMON_WHEEL_TICKET]: null,
    [InGameCurrency.WHEEL_TICKET]: <Picture url={wheelTicketUrl} />,
    [InGameCurrency.COMPETITION_CRAFT_CLUE]: (
      <Picture url={competitionCraftClueUrl} />
    ),
    [InGameCurrency.DIVISION_CRAFT_CLUE]: <Picture url={divisionClueUrl} />,
    [InGameCurrency.POSITION_CRAFT_CLUE]: <Picture url={positionClueUrl} />,
    [InGameCurrency.COUNTRY_CRAFT_CLUE]: <Picture url={countryCraftClueUrl} />,
    [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: (
      <Picture url={fiftyFiftyCraftClueUrl} />
    ),
    [InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE]: (
      <Picture url={potionCraftClueUrl} />
    ),
    [InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE]: (
      <Picture url={bestStarRankCraftClueUrl} />
    ),
  }[currency];
};

InGameCurrency3d.fragments = {
  InGameCurrencyRewardConfig: gql`
    fragment InGameCurrency3d_inGameCurrencyReward on InGameCurrencyRewardConfig {
      id
      currency
    }
  ` as TypedDocumentNode<InGameCurrency3d_inGameCurrencyReward>,
  ProbabilisticBundleSlotInGameCurrencyItem: gql`
    fragment InGameCurrency3d_ProbabilisticBundleSlotInGameCurrencyItem on ProbabilisticBundleSlotInGameCurrencyItem {
      id
      currency
    }
  ` as TypedDocumentNode<InGameCurrency3d_ProbabilisticBundleSlotInGameCurrencyItem>,
};
